import { z } from "zod/v4";

import {
  CreateOnsiteInjectionDtoDescriptions,
  JsInjectionModeDtoDescriptions,
  OnsiteInjectionDtoDescriptions,
} from "../descriptions/onsite-injection-dto.describe";
import { assertDescriptions } from "../descriptions/types";
import { castToNumber } from "../utils";

import { applyJsInjectionModeRefinements } from "./onsite-injections.refinements";
import { ModificationSourceList } from "./modification-source";

export const OnsiteInjectionsCodeTypeList = ["customCss", "customJs"] as const;

export type OnsiteInjectionsCodeType =
  (typeof OnsiteInjectionsCodeTypeList)[number];

export const JsInjectionModeTypeList = [
  "immediately",
  "onWindowLoad",
  "timeout",
] as const;

export type JsInjectionModeType = (typeof JsInjectionModeTypeList)[number];

// Base schema with object structure - use discriminated union for stricter validation
const JsInjectionModeBaseSchema = z.object({
  type: z
    .enum(JsInjectionModeTypeList)
    .default("immediately")
    .describe(JsInjectionModeDtoDescriptions.type),
  timeout: castToNumber
    .optional()
    .describe(JsInjectionModeDtoDescriptions.timeout),
});

// DTO schema without refinements - safe for GET responses with existing data
export const JsInjectionModeDtoSchema = JsInjectionModeBaseSchema;
export type JsInjectionModeDto = z.infer<typeof JsInjectionModeDtoSchema>;

// Write schema with refinements: timeout required and non-negative when type is 'timeout'
export const WriteJsInjectionModeDtoSchema = applyJsInjectionModeRefinements(
  JsInjectionModeBaseSchema
);

// Base schema without refinements - use for .omit()/.extend() operations
// NOTE: customCss and customJs are intentionally nullish here because the database stores null
// when no CSS/JS has been configured for a variation. Consumers that require non-null strings
// (e.g., the MCP tool output schema in web/ai analyze_experience) must coerce null -> "" before
// schema validation. See analyze-resource-jwt.tool.ts for the required coercion pattern.
export const OnsiteInjectionBaseSchema = z.object({
  id: z.uuid().describe(OnsiteInjectionDtoDescriptions.id),
  variationId: z.uuid().describe(OnsiteInjectionDtoDescriptions.variationId),
  customCss: z
    .preprocess(
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      (x) => (typeof x === "object" ? x?.toString() : x),
      z.string().nullish()
    )
    .describe(OnsiteInjectionDtoDescriptions.customCss),
  // add new key for custom js injection mode
  customJs: z
    .preprocess(
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      (x) => (typeof x === "object" ? x?.toString() : x),
      z.string().nullish()
    )
    .describe(OnsiteInjectionDtoDescriptions.customJs),
  jsInjectionMode: JsInjectionModeBaseSchema.describe(
    OnsiteInjectionDtoDescriptions.jsInjectionMode
  ),
  source: z.enum(ModificationSourceList).nullish(),
});

// DTO schema without refinements - safe for GET responses with existing data
export const OnsiteInjectionDtoSchema = OnsiteInjectionBaseSchema;

export type OnsiteInjectionDto = z.infer<typeof OnsiteInjectionDtoSchema>;

export const CreateOnsiteInjectionDtoSchema = OnsiteInjectionBaseSchema.extend({
  id: z.uuid().optional().describe(CreateOnsiteInjectionDtoDescriptions.id),
  customCss: OnsiteInjectionBaseSchema.shape.customCss.describe(
    CreateOnsiteInjectionDtoDescriptions.customCss
  ),
  customJs: OnsiteInjectionBaseSchema.shape.customJs.describe(
    CreateOnsiteInjectionDtoDescriptions.customJs
  ),
  jsInjectionMode: WriteJsInjectionModeDtoSchema.describe(
    CreateOnsiteInjectionDtoDescriptions.jsInjectionMode
  ),
});

export type CreateOnsiteInjectionDto = z.infer<
  typeof CreateOnsiteInjectionDtoSchema
>;

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<typeof JsInjectionModeDtoDescriptions, JsInjectionModeDto>();
assertDescriptions<typeof OnsiteInjectionDtoDescriptions, OnsiteInjectionDto>();
